import { useWeb3React } from '@web3-react/core'
import {} from 'components'
import { configConstants } from 'config'
import { ColorType } from 'constants/colors'
import { useCreditContext } from 'contexts'
import { BigNumber } from 'ethers'
import { useCapsulesLoading, useResponsive } from 'hooks'
import _, { cloneDeep } from 'lodash'
import { useEffect, useState } from 'react'
import { bigDiv, bigMul } from 'utils'
import { CoreCapsuleWithAccount } from '../../../entities'
import { SummaryDesktop } from './SummaryDesktop'
import { SummaryMobile } from './SummaryMobile'

export interface ISummaryProps {
  totalDeposited: BigNumber
  totalBorrowed: BigNumber
  interestRate: BigNumber
  borrowUsage: BigNumber
  positionTooltip: string
  borrowTooltip: string
  suggestionText: string
  suggestionIcon: JSX.Element
  usageTextColor: ColorType
  coreCapsules: CoreCapsuleWithAccount[]
}

const defaultProps: ISummaryProps = {
  borrowTooltip:
    'Represents borrow amount relative to available credit and determines how fast your credit score grows',
  positionTooltip: 'Top-level metrics across all active vaults',
  totalDeposited: BigNumber.from(0),
  totalBorrowed: BigNumber.from(0),
  interestRate: BigNumber.from(0),
  borrowUsage: BigNumber.from(0),
  suggestionText: '',
  suggestionIcon: <></>,
  usageTextColor: 'GREEN_5',
  coreCapsules: [],
}

export const PositionSummary = () => {
  const { width } = useResponsive()
  const { account } = useWeb3React()
  const { coreCapsules, isGlobalRefreshing } = useCreditContext()
  const [summaryProps, setSummaryProps] = useState<ISummaryProps>(defaultProps)
  const isLoading = useCapsulesLoading(Array.from(coreCapsules.values())) || isGlobalRefreshing

  useEffect(() => {
    if (coreCapsules.size === 0 || isLoading || !account) {
      if (!_.isEqual(summaryProps, defaultProps)) {
        setSummaryProps(defaultProps)
      }

      return
    }
    const localProps = cloneDeep(defaultProps)
    localProps.coreCapsules = Array.from(coreCapsules.values())
    // First pass gets totals to use in weighted average
    for (const coreCapsule of coreCapsules.values()) {
      const { accountProperties } = coreCapsule
      if (accountProperties) {
        localProps.totalBorrowed = localProps.totalBorrowed.add(
          accountProperties.borrowedAmountScaled,
        )
        localProps.totalDeposited = localProps.totalDeposited.add(accountProperties.collateralValue)
      }
    }

    // Second pass gets weighted averages of interest rate and borrow usage
    for (const coreCapsule of coreCapsules.values()) {
      const { accountProperties, yearlyInterestRate } = coreCapsule
      if (accountProperties) {
        const ratio = localProps.totalBorrowed.isZero()
          ? BigNumber.from(0)
          : bigDiv(accountProperties.borrowedAmountScaled, localProps.totalBorrowed)

        localProps.interestRate = localProps.interestRate.add(bigMul(yearlyInterestRate, ratio))
        localProps.borrowUsage = localProps.borrowUsage.add(
          bigMul(accountProperties.borrowUsage, ratio),
        )
      }
    }

    localProps.usageTextColor = localProps.borrowUsage.gt(configConstants.atRiskThreshold)
      ? 'RED_5'
      : 'GREEN_5'
    setSummaryProps(localProps)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account, coreCapsules, isLoading])

  return width < 800 ? <SummaryMobile {...summaryProps} /> : <SummaryDesktop {...summaryProps} />
}
